import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useNavigate } from 'react-router-dom';
import { User } from '../../../hooks/admin/useUsers';
import UserSearchForm from './UserSearchForm';
import UserTable from './UserTable';

interface SearchParams {
  searchTerm: string;
  userLevel?: number;
  status?: string;
}

interface ExtendedUser extends User {
  id: string;
}

const UserManagePage: React.FC = () => {
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ExtendedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    searchTerm: '',
    userLevel: undefined,
    status: ''
  });
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();

  // Firestore Timestamp를 Date로 변환하는 헬퍼 함수
  const convertTimestamp = useCallback((timestamp: any): string => {
    if (!timestamp) return '';
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }
    if (typeof timestamp === 'number') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? '' : date.toISOString();
    }
    if (typeof timestamp === 'string') {
      return timestamp;
    }
    return '';
  }, []);

  // 사용자 목록 가져오기
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, 'user_profiles'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const usersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const rawUserLevel = data.userLevel;
        const parsedUserLevel =
          typeof rawUserLevel === 'number'
            ? rawUserLevel
            : typeof rawUserLevel === 'string'
              ? Number(rawUserLevel)
              : NaN;

        return {
          id: doc.id,
          uid: data.uid || doc.id,
          name: data.name || '이름 없음',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          organization: data.organization || '',
          role: data.role || '',
          status: data.status || 'active',
          userLevel: Number.isFinite(parsedUserLevel) ? parsedUserLevel : 9,
          lastLoginAt: convertTimestamp(data.lastLoginAt),
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      });

      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (err) {
      console.error('사용자 목록 조회 실패:', err);
      setError(err instanceof Error ? err.message : '사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [convertTimestamp]);

  // 권한 체크 및 초기 데이터 로드
  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 사용자 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.users)) {
        navigate('/admin/dashboard');
        return;
      }

      fetchUsers();
    }
  }, [user, userLevel, permissions, authLoading, navigate, fetchUsers]);

  // 검색 파라미터 업데이트
  const handleSearch = useCallback((params: SearchParams) => {
    setSearchParams(params);
  }, []);

  // 필터링된 사용자 목록 (메모이제이션)
  const filteredUsersList = useMemo(() => {
    let filtered = [...users];

    // 검색어로 필터링
    if (searchParams.searchTerm.trim()) {
      const searchLower = searchParams.searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.organization.toLowerCase().includes(searchLower) ||
        user.phoneNumber.includes(searchParams.searchTerm)
      );
    }

    // 권한(레벨)로 필터링
    if (typeof searchParams.userLevel === 'number') {
      filtered = filtered.filter(user => user.userLevel === searchParams.userLevel);
    }

    // 상태로 필터링
    if (searchParams.status) {
      filtered = filtered.filter(user => user.status === searchParams.status);
    }

    return filtered;
  }, [users, searchParams]);

  // 필터링된 사용자 상태 업데이트
  useEffect(() => {
    setFilteredUsers(filteredUsersList);
  }, [filteredUsersList]);

  // 로딩 상태 렌더링
  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">사용자 데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 렌더링
  if (error) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-bold text-red-800 mb-2">데이터 로드 실패</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => fetchUsers()}
                className="w-[120px] h-10 bg-red-600 text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              사용자 관리
            </div>
            <button
              onClick={() => navigate('/admin/users/add')}
              className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
            >
              회원 추가
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>
        
        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard'] mb-2">
                  시스템에 등록된 사용자를 관리하고 권한을 설정할 수 있습니다.
                </p>
                <p className="text-[#6b7684] text-sm">
                  사용자 검색, 상태 변경, 권한 관리 등의 기능을 제공합니다.
                </p>
              </div>
              <button
                onClick={() => fetchUsers()}
                className="flex items-center gap-2 px-3 py-2 text-[#155acb] hover:bg-[#155acb]/5 rounded transition-colors"
                title="목록 새로고침"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                새로고침
              </button>
            </div>
          </div>
        </div>
        
        {/* 검색 폼 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              사용자 검색
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            <UserSearchForm onSearch={handleSearch} />
          </div>
        </div>
        
        {/* 사용자 목록 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                사용자 목록
              </h2>
              <div className="text-sm text-[#6b7684]">
                총 {users.length}명 / 검색결과 {filteredUsers.length}명
              </div>
            </div>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            {filteredUsers.length === 0 && searchParams.searchTerm ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-bold text-[#1e2c49] mb-2">검색 결과가 없습니다</h3>
                <p className="text-[#6b7684] mb-4">'{searchParams.searchTerm}'에 대한 검색 결과를 찾을 수 없습니다.</p>
                <button
                  onClick={() => handleSearch({ searchTerm: '', userLevel: undefined, status: '' })}
                  className="text-[#155acb] hover:text-[#155acb]/80 font-medium"
                >
                  검색 초기화
                </button>
              </div>
            ) : filteredUsers.length === 0 && !searchParams.searchTerm ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <h3 className="text-lg font-bold text-[#1e2c49] mb-2">등록된 사용자가 없습니다</h3>
                <p className="text-[#6b7684] mb-4">아직 시스템에 등록된 사용자가 없습니다.</p>
                <button
                  onClick={() => navigate('/admin/users/add')}
                  className="w-[130px] h-10 bg-[#155acb] text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                >
                  첫 회원 추가
                </button>
              </div>
            ) : (
              <UserTable users={filteredUsers} />
            )}
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default UserManagePage;
